/**
 * ErrorBoundary Component
 * 
 * A React error boundary that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI instead of
 * crashing the entire application.
 * 
 * Requirements: 13.5
 */

import React, { Component } from 'react';
import type { ReactNode } from 'react';
import type { AppError } from '../utils/errorHandling';
import { handleError, logError } from '../utils/errorHandling';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: AppError, resetError: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
}

/**
 * ErrorBoundary component that catches and handles React component errors
 * 
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 * 
 * @example With custom fallback
 * ```tsx
 * <ErrorBoundary fallback={(error, reset) => (
 *   <div>
 *     <h1>Custom Error UI</h1>
 *     <p>{error.message}</p>
 *     <button onClick={reset}>Try Again</button>
 *   </div>
 * )}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  /**
   * Static method called when an error is thrown in a child component
   * Updates state to trigger fallback UI rendering
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const appError = handleError(error);
    return {
      hasError: true,
      error: appError
    };
  }

  /**
   * Lifecycle method called after an error is caught
   * Logs the error with additional context
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const appError = handleError(error);
    logError(appError, {
      componentStack: errorInfo.componentStack,
      errorBoundary: 'ErrorBoundary'
    });
  }

  /**
   * Resets the error boundary state to allow retry
   */
  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default fallback UI
      return <ErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

/**
 * Default fallback UI component displayed when an error is caught
 */
interface ErrorFallbackProps {
  error: AppError;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps): React.ReactElement {
  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="error-boundary" role="alert" aria-live="assertive">
      <div className="error-boundary__container">
        <div className="error-boundary__icon" aria-hidden="true">
          ⚠️
        </div>
        
        <h1 className="error-boundary__title">
          Something went wrong
        </h1>
        
        <p className="error-boundary__message">
          {error.message}
        </p>

        {error.retryable && (
          <button
            className="error-boundary__button"
            onClick={resetError}
            aria-label="Try again"
          >
            Try Again
          </button>
        )}

        {!error.retryable && (
          <button
            className="error-boundary__button"
            onClick={() => window.location.href = '/'}
            aria-label="Go to home page"
          >
            Go to Home
          </button>
        )}

        {isDevelopment && error.originalError && (
          <details className="error-boundary__details">
            <summary>Error Details (Development Only)</summary>
            <pre className="error-boundary__stack">
              {error.originalError.stack || error.originalError.message}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default ErrorBoundary;
